import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { errorDetail, useDelivery } from "../hooks";
import { DeliveryPicker } from "../cart/DeliveryPicker";
import { useConfirm } from "../../../components/common/ConfirmDialog";

const Redate = ({ value, onChange }) => {
  const { t } = useI18n();
  const delivery = useDelivery();
  return (
    <div className="mb-3 rounded-xl bg-white p-3" data-testid="approve-redate">
      <p className="mb-2 text-sm font-medium text-amber-800">{t("checkout.dateInvalid")}</p>
      <DeliveryPicker delivery={delivery} value={value} onChange={onChange} />
    </div>
  );
};

export const ApprovalActions = ({ order }) => {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null);
  const [redate, setRedate] = useState(false);
  const [date, setDate] = useState(null);
  const confirm = useConfirm();

  const act = async (kind) => {
    if (kind === "reject" && !(await confirm({ title: `${t("orders.reject")}?`, danger: true, confirmLabel: t("orders.reject") }))) return;
    if (kind === "approve" && redate && !date) { toast.error(t("checkout.pickDate")); return; }
    setBusy(kind);
    try {
      const body = kind === "approve" ? (date ? { requested_delivery_date: date } : {}) : undefined;
      const { data } = await api.post(`/orders/${order.local_id}/${kind}`, body);
      toast.success(kind === "approve" ? t("orders.approved", { doc: data.doc_no }) : t("orders.rejected"));
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["order"] });
    } catch (e) {
      const stale = errorDetail(e) === "delivery_date_invalid";
      if (stale) { setRedate(true); setDate(null); }
      toast.error(stale ? t("checkout.dateInvalid") : t("checkout.failed"));
    } finally { setBusy(null); }
  };

  const base = "flex h-11 flex-1 items-center justify-center gap-1.5 rounded-full text-sm font-semibold disabled:opacity-60";
  return (
    <div data-testid={`approval-actions-${order.local_id}`}>
      {redate && <Redate value={date} onChange={setDate} />}
      <div className="flex gap-2">
        <button type="button" disabled={!!busy} onClick={() => act("reject")} data-testid={`reject-${order.local_id}`} className={`${base} border border-rose-200 text-rose-700 hover:bg-rose-50`}>
          {busy === "reject" ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />} {t("orders.reject")}
        </button>
        <button type="button" disabled={!!busy} onClick={() => act("approve")} data-testid={`approve-${order.local_id}`} className={`${base} bg-brand text-white`}>
          {busy === "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t("orders.approve")}
        </button>
      </div>
    </div>
  );
};
